<template>
	<q-layout-drawer side="left" v-model="showMenu" v-if="$q.platform.is.mobile">
		<q-list no-border link inset-separator>
			<q-list-header>{{ $t('navigation') }}</q-list-header>

			<global-search />

			<span v-for="(link, index) in getNavbarOrder" :key="index">
				<q-item :to="{ name: link.to }" v-if="link.to">
					<q-item-side :icon="link.icon" />
					<q-item-main :label="$t(link.text)" />
				</q-item>

				<q-collapsible
					icon="far fa-users-class"
					:label="$t(link.text)"
					group="sidebar"
					v-model="show_merged"
					v-if="!link.to"
				>
					<q-list separator link>
						<q-item
							:to="{ name: b.to }"
							v-for="(b, index) in link.buttons"
							:key="`b-${index}`"
							@click.native="show_merged = false"
						>
							<q-item-main>
								<q-item-tile label>{{ $t(b.text) }}</q-item-tile>
							</q-item-main>
						</q-item>
					</q-list>
				</q-collapsible>
			</span>
		</q-list>

		<q-list no-border>
			<q-list-header>{{ $t('others') }}</q-list-header>

			<q-collapsible
				icon="far fa-building"
				:label="getClientLabel"
				group="sidebar"
				v-model="show_clients"
				v-if="user_clients.length > 1"
			>
				<q-list separator link>
					<q-item tag="label" v-for="(item, index) in user_clients" :key="index">
						<q-item-side>
							<q-radio v-model="selected_client" :val="item.id" />
						</q-item-side>
						<q-item-main>
							<q-item-tile label>{{ item.name }}</q-item-tile>
						</q-item-main>
					</q-item>
				</q-list>
			</q-collapsible>

			<q-item v-if="user_clients.length == 1">
				<q-item-side icon="far fa-building" />
				<q-item-main :label="getClientLabel" />
			</q-item>

			<q-collapsible
				icon="far fa-globe"
				:label="getCountryLabel"
				group="sidebar"
				v-model="show_countries"
				v-if="countries.length > 1"
			>
				<q-list separator link>
					<q-item tag="label" v-for="(item, index) in countries" :key="index">
						<q-item-side>
							<q-radio v-model="selected_country" :val="item.id" />
						</q-item-side>
						<q-item-main>
							<q-item-tile label>{{ item.name }}</q-item-tile>
						</q-item-main>
					</q-item>
				</q-list>
			</q-collapsible>

			<q-item v-if="countries.length == 1">
				<q-item-side icon="far fa-globe" />
				<q-item-main :label="getCountryLabel" />
			</q-item>

			<q-item :to="{ name: 'admin_overview' }" v-if="showControlPanel">
				<q-item-side icon="far fa-tachometer-alt" />
				<q-item-main :label="$t('access_panel')" />
			</q-item>

			<q-collapsible icon="far fa-user-circle" :label="full_name" group="sidebar">
				<q-list separator link>
					<q-item @click.native="goToMessages()">
						<q-item-side>
							<q-icon name="far fa-envelope" />
						</q-item-side>
						<q-item-main>
							<q-item-tile label>{{ $t('messages') }}</q-item-tile>
						</q-item-main>
					</q-item>
					<q-item @click.native="goToProfile()" v-if="showUserProfile">
						<q-item-side>
							<q-icon name="far fa-cog" />
						</q-item-side>
						<q-item-main>
							<q-item-tile label>{{ $t('edit_profile') }}</q-item-tile>
						</q-item-main>
					</q-item>
					<q-item @click.native="showChangePassword()">
						<q-item-side>
							<q-icon name="far fa-key" />
						</q-item-side>
						<q-item-main>
							<q-item-tile label>{{ $t('change_password') }}</q-item-tile>
						</q-item-main>
					</q-item>
					<q-item @click.native="logout()">
						<q-item-side>
							<q-icon name="far fa-sign-out-alt" />
						</q-item-side>
						<q-item-main>
							<q-item-tile label>{{ $t('sign_out') }}</q-item-tile>
						</q-item-main>
					</q-item>
				</q-list>
			</q-collapsible>
		</q-list>

		<change-password
			:show_change_password="show_change_password"
			@hide_modal="show_change_password = false"
		/>

		<copyright />
	</q-layout-drawer>
</template>

<script>
import { mapGetters } from 'vuex'
import Copyright from 'components/Copyright'
import ChangePassword from 'components/ChangePassword'
import firebase from 'firebase/app'
import 'firebase/app'
import 'firebase/auth'
import GlobalSearch from 'components/header/GlobalSearch'

export default {
	i18nOptions: { namespaces: ['custom', 'common', 'authenticated'] },

	components: {
		copyright: Copyright,
		'change-password': ChangePassword,
		'global-search': GlobalSearch,
	},

	data() {
		return {
			show_clients: false,
			countries: [],
			show_countries: false,
			show_change_password: false,
			show_merged: false,
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			clients: 'params/clients',
			full_name: 'common/full_name',
			id_user_client_country_selected: 'common/id_user_client_country_selected',
			id_user_client_selected: 'common/id_user_client_selected',
			id_user_language_selected: 'common/id_user_language_selected',
			left_menu: 'common/left_menu',
			reload_content: 'common/reload_content',
			user_change_password: 'common/user_change_password',
			user_clients: 'common/user_clients',
			user_module_items: 'common/user_module_items',
		}),

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},

		getCountryLabel() {
			const obj = this.countries.find((obj) => obj.id == this.id_user_client_country_selected)
			return obj ? obj.name : ''
		},

		getClientLabel() {
			const obj = this.user_clients.find((obj) => obj.id == this.id_user_client_selected)
			return obj ? obj.name : ''
		},

		showControlPanel() {
			let allow = false
			if (this.user_module_items) {
				const obj = this.user_module_items.find(
					(obj) => obj.slug == 'overview' && obj.permission == true
				)
				if (obj) allow = true
			}
			return allow
		},

		showAddTraining() {
			let allow = false
			const obj = this.user_module_items.find(
				(obj) => obj.slug == 'trainings' && obj.permission == true
			)
			if (obj) allow = true
			return allow
		},

		showUserProfile() {
			if (this.id_user_client_selected) {
				const obj = this.clients.find((obj) => obj.id === this.id_user_client_selected)
				if (obj) {
					if (obj.has_profile) return true
				}
			}
			return false
		},

		selected_country: {
			get() {
				return this.id_user_client_country_selected
			},
			set(val) {
				this.$store.dispatch('common/SET_COUNTRY', [val])
				this.show_countries = !this.show_countries
				this.setUserSelection()
			},
		},

		selected_client: {
			get() {
				return this.id_user_client_selected
			},
			set(val) {
				this.$store.dispatch('common/SET_CLIENT', [val])
				this.show_clients = !this.show_clients
				this.getCountryList()
				this.setUserSelection()
			},
		},

		showMenu: {
			get() {
				return this.left_menu
			},
			set() {
				this.$store.dispatch('common/SET_LEFT_MENU', [false])
			},
		},

		getNavbarOrder() {
			let navbar = []

			let obj = this.clients.find((obj) => {
				return obj.id === this.id_user_client_selected
			})
			if (obj) {
				if (obj.has_home) {
					navbar.push({
						icon: 'far fa-home',
						to: 'home',
						text: obj.title_home == '' ? 'home' : obj.title_home,
					})
				}

				if (!obj.has_merged_content) {
					if (obj.has_courses) {
						navbar.push({
							icon: 'far fa-book-reader',
							to: 'courses',
							text: obj.title_trainings == '' ? 'courses' : obj.title_trainings,
						})
					}

					if (obj.has_trails) {
						navbar.push({
							icon: 'far fa-bezier-curve',
							to: 'trails',
							text: obj.title_trails == '' ? 'trails' : obj.title_trails,
						})
					}

					if (obj.has_presential) {
						navbar.push({
							icon: 'far fa-users-class',
							to: 'presential',
							text: obj.title_presential == '' ? 'presential' : obj.title_presential,
						})
					}

					if (obj.has_webcasts) {
						navbar.push({
							icon: 'far fa-broadcast-tower',
							to: 'webcasts',
							text: obj.title_webcasts == '' ? 'webcasts' : obj.title_webcasts,
						})
					}

					if (obj.has_podcasts) {
						navbar.push({
							icon: 'far fa-podcast',
							to: 'podcasts',
							text: obj.title_podcasts == '' ? 'podcasts' : obj.title_podcasts,
						})
					}

					if (obj.has_live_classes) {
						navbar.push({
							icon: 'fal fa-chalkboard-teacher',
							to: 'live_classes',
							text:
								obj.title_live_classes == ''
									? 'live_classes'
									: obj.title_live_classes,
						})
					}
				} else {
					let buttons = []

					if (obj.has_courses) {
						buttons.push({
							icon: 'far fa-book-reader',
							to: 'courses',
							text: obj.title_trainings == '' ? 'courses' : obj.title_trainings,
						})
					}

					if (obj.has_trails) {
						buttons.push({
							icon: 'far fa-bezier-curve',
							to: 'trails',
							text: obj.title_trails == '' ? 'trails' : obj.title_trails,
						})
					}

					if (obj.has_presential) {
						buttons.push({
							icon: 'far fa-users-class',
							to: 'presential',
							text: obj.title_presential == '' ? 'presential' : obj.title_presential,
						})
					}

					if (obj.has_webcasts) {
						buttons.push({
							icon: 'far fa-broadcast-tower',
							to: 'webcasts',
							text: obj.title_webcasts == '' ? 'webcasts' : obj.title_webcasts,
						})
					}

					if (obj.has_live_classes) {
						buttons.push({
							icon: 'fal fa-chalkboard-teacher',
							to: 'live_classes',
							text:
								obj.title_live_classes == ''
									? 'live_classes'
									: obj.title_live_classes,
						})
					}

					if (obj.has_podcasts) {
						buttons.push({
							icon: 'far fa-podcast',
							to: 'podcasts',
							text: obj.title_podcasts == '' ? 'podcasts' : obj.title_podcasts,
						})
					}

					navbar.push({
						icon: 'far fa-broadcast-tower',
						text: obj.merged_content_title
							? this.$t(obj.merged_content_title)
							: this.$t('trainings'),
						buttons: buttons,
						to: '',
					})
				}

				if (obj.has_library) {
					navbar.push({
						icon: 'far fa-books',
						to: 'library',
						text: obj.title_library == '' ? 'library' : obj.title_library,
					})
				}

				if (obj.has_news) {
					navbar.push({
						icon: 'far fa-newspaper',
						to: 'news',
						text: obj.title_news == '' ? 'news' : obj.title_news,
					})
				}

				if (obj.has_feed) {
					navbar.push({
						icon: 'far fa-comment-alt-smile',
						to: 'feed',
						text: obj.title_feed == '' ? 'feed' : obj.title_feed,
					})
				}

				if (obj.has_faq) {
					navbar.push({
						icon: 'far fa-question-square',
						to: 'faq',
						text: obj.title_faq == '' ? 'faq' : obj.title_faq,
					})
				}

				if (obj.has_profile) {
					navbar.push({
						icon: 'far fa-user',
						to: 'profile',
						text: obj.title_profile == '' ? 'profile' : obj.title_profile,
					})
				}

				let index = navbar.findIndex((x) => x.to === obj.home_route)
				if (index && index != 0) {
					navbar.unshift(
						navbar.splice(navbar.findIndex((item) => item.to === obj.home_route), 1)[0]
					)
				}
			}

			return navbar
		},
	},

	methods: {
		getCountryList() {
			this.countries = []

			if (this.user_clients) {
				let obj = this.user_clients.find((obj) => {
					return obj.id == this.id_user_client_selected
				})

				if (obj) {
					this.countries = obj.countries

					let obj_countries = this.countries.find((obj) => {
						return obj.id == this.id_user_client_country_selected
					})
					if (!obj_countries) {
						this.$store.dispatch('common/SET_COUNTRY', [this.countries[0].id])
						this.setUserSelection()
					}
				}
			}
		},

		logout() {
			this.showMenu = false
			this.$store.dispatch('common/SET_ACCESS_TOKEN', ['', '', ''])

			firebase
				.auth()
				.signOut()
				.then(() => {})
				.catch((e) => {
					console.log(e)
				})

			setTimeout(() => {
				this.$q.loading.hide()
				this.$router.replace({ name: 'login' })
			}, 700)
		},

		setUserSelection() {
			this.$q.loading.show()

			let fd = new FormData()
			fd.append('client', this.id_user_client_selected)
			fd.append('country', this.id_user_client_country_selected)
			fd.append('language', this.id_user_language_selected)

			this.$axios
				.post('/accounts/set_user_selection/', fd, this.getHeader)
				.then(() => {
					this.getUserData()
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		getUserData() {
			this.$axios
				.get('/accounts/read_user_data/', this.getHeader)
				.then((response) => {
					const d = response.data

					this.$store.dispatch('common/SET_USER_DATA', [
						d.id,
						d.id_user_client_profile,
						d.full_name,
						d.document,
						d.id_user_client_selected,
						d.id_user_client_country_selected,
						d.id_user_language_selected,
						d.user_clients,
						d.user_client_module_items,
						d.user_firebase_document,
						d.is_superuser,
						d.email,
						d.user_firebase_document_ranking_areas,
						d.has_mandatory_surveys,
						d.accept_terms_and_conditions,
						d.user_companies,
						d.user_branches,
					])

					this.$store.dispatch('common/SET_RELOAD_CONTENT', [!this.reload_content])
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		goToMessages() {
			this.show_user_menu = false
			this.$router.push({ name: 'messages' })
		},

		goToProfile() {
			this.show_user_menu = false
			this.$router.push({ name: 'profile' })
		},

		showChangePassword() {
			this.show_user_menu = false
			this.show_change_password = true
		},
	},

	watch: {
		id_user_client_selected: function() {
			if (this.$route.name != 'admin_overview' && this.$q.platform.is.mobile) {
				let obj_redirect = this.clients.find((obj) => {
					return obj.id === this.id_user_client_selected
				})
				if (obj_redirect) {
					if (obj_redirect.home_route)
						this.$router.replace({ name: obj_redirect.home_route })
					else {
						this.$router.replace({ name: 'courses' })
					}
				} else {
					this.$router.replace({ name: 'courses' })
				}
			}
		},

		id_user_client_country_selected: function() {
			if (this.$route.name != 'admin_overview' && this.$q.platform.is.mobile) {
				const obj_redirect = this.clients.find(
					(obj) => obj.id === this.id_user_client_selected
				)
				if (obj_redirect) {
					if (obj_redirect.home_route)
						this.$router.replace({ name: obj_redirect.home_route })
					else {
						this.$router.replace({ name: 'courses' })
					}
				} else {
					this.$router.replace({ name: 'courses' })
				}
			}
		},
	},

	created() {
		this.getCountryList()

		if (this.user_change_password) this.show_change_password = true
	},
}
</script>

<style></style>
